import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import styles from './ContactPressForm.module.scss';
import ButtonPrimary from '../ButtonPrimary';
import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import TextareaAutosize from 'react-textarea-autosize';
import { Dialog, DialogDismiss, DialogHeading, useDialogState } from 'ariakit/dialog';
const ContactPressForm = ({ formProps, csrf }) => {
    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            help: formProps?.help?.defaultValue,
            contact: 'email',
            type: '',
        },
    });
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [submitting, setSubmitting] = useState(false);
    const [links, setLinks] = useState(['']);
    const dialog = useDialogState({
        animated: true,
    });
    const [ref, isVisible] = useIntersectionObserver();

    useEffect(() => {
        if (!dialog.open) {
            reset();
        }
    }, [dialog.open]);

    const URL = '/wt/api/nextjs/v1/send_contact_form/?type=media';

    const handleSubmitForm = async (data) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            if (!executeRecaptcha) throw new Error('reCAPTCHA not ready');
            const token = await executeRecaptcha('press_form');
            if (!token) throw new Error('Failed to get reCAPTCHA token');

            const formData = new FormData();
            formData.append('recaptcha_token', token);
            formData.append('name', data.name);
            formData.append('email', data.email);
            formData.append('phonenumber', data.phonenumber);
            formData.append('type', data.type);
            formData.append('when', data.when);
            formData.append('how', data.how);

            let linkText = '';
            for (let i = 0; i < links.length; i++) {
                linkText += (data[`link${i}`] || '') + '\r\n';
            }
            formData.append('links', linkText);

            const res = await fetch(URL, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'X-CSRFToken': csrf,
                },
                body: formData,
                credentials: 'include',
            });

            if (!res.ok) {
                const parsed = await res.json().catch(() => null);
                console.error('Submit failed', parsed);
                throw new Error(parsed?.message || 'reCAPTCHA verification failed.');
            }

            dialog.show();
        } catch (error) {
            console.error('Error:', error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const contact = watch('type');
    const { t } = useTranslation();

    const formClasses = classNames(styles['Root'], 'black-glass', {
        [styles['Visible']]: isVisible,
    });

    return (
        <>
            <form onSubmit={handleSubmit(handleSubmitForm)} noValidate ref={ref} className={formClasses}>
                <div className={styles['FieldsetWrapper']}>
                    <p className={styles['Description']}>{t('ContactForm.description')}</p>
                    <fieldset className={styles['Fieldset']}>
                        <legend className={styles['Legend']}>{t('ContactPressForm.legends.contactInfo')}</legend>

                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('name'),
                                    [styles['HasError']]: errors.name,
                                })}>
                                <input
                                    id="name"
                                    className={styles['Input']}
                                    autoComplete="name"
                                    aria-invalid={!!errors.name}
                                    aria-describedby={errors.name ? 'name-error' : null}
                                    maxLength={70}
                                    {...register('name', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="name">
                                    {t('ContactPressForm.labels.name')} *
                                </label>
                            </div>
                            {errors.name && <ErrorMessage id="name-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('email'),
                                    [styles['HasError']]: errors.email,
                                })}>
                                <input
                                    id="email"
                                    className={styles['Input']}
                                    autoComplete="email"
                                    maxLength={150}
                                    aria-invalid={!!errors.email}
                                    aria-describedby={errors.email ? 'email-error' : null}
                                    {...register('email', {
                                        required: 'This field is required',
                                        pattern: {
                                            value: /\S+@\S+\.\S+/,
                                            message: 'Enter a valid e-mail',
                                        },
                                    })}
                                />
                                <label className={styles['Label']} htmlFor="email">
                                    {t('ContactPressForm.labels.email')} *
                                </label>
                            </div>
                            {errors.email && <ErrorMessage id="email-error">{errors.email.message}</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('phonenumber'),
                                    [styles['HasError']]: errors.phonenumber,
                                })}>
                                <input
                                    className={styles['Input']}
                                    id="phonenumber"
                                    maxLength={30}
                                    autoComplete="tel"
                                    aria-labelledby="phoneNum phoneHelp"
                                    aria-invalid={!!errors.phonenumber}
                                    aria-describedby={errors.phonenumber ? 'phonenumber-error' : null}
                                    {...register('phonenumber', {
                                        required: true,
                                    })}
                                />
                                <label id="phoneNum" className={styles['Label']} htmlFor="phonenumber">
                                    {t('ContactPressForm.labels.phone')} *
                                </label>
                            </div>
                            <div id="phoneHelp" className={styles['HelpText']}>
                                {t('ContactPressForm.help.phone')}
                            </div>
                            {errors.phonenumber && (
                                <ErrorMessage id="phonenumber-error">This field is required</ErrorMessage>
                            )}
                        </div>
                    </fieldset>
                    <div className={styles['FieldsetWrapper']}>
                        <fieldset className={styles['Fieldset']}>
                            <legend className={styles['Legend']}>{t('ContactPressForm.legends.link')}</legend>
                            {links.map((link, index) => (
                                <div key={index} className={styles['Field']}>
                                    <div
                                        className={classNames(styles['InputField'], {
                                            [styles['HasValue']]: watch(`link${index}`),
                                        })}>
                                        <input
                                            id={`link${index}`}
                                            className={styles['Input']}
                                            maxLength={170}
                                            aria-invalid={!!errors[`link${index}`]}
                                            aria-describedby={errors[`link${index}`] ? `link${index}-error` : null}
                                            {...register(`link${index}`)}
                                        />
                                        <label className={styles['Label']} htmlFor={`link${index}`}>
                                            {t('ContactPressForm.labels.link')}
                                        </label>
                                    </div>
                                    {errors[`link${index}`] && (
                                        <ErrorMessage id={`link${index}-error`}>This field is required</ErrorMessage>
                                    )}
                                </div>
                            ))}
                            <div className={styles['ButtonWrap']}>
                                <button
                                    type="button"
                                    className={styles['Button']}
                                    onClick={() => {
                                        setLinks([...links, '']);
                                    }}>
                                    <i className={styles['PlusIcon']} />
                                    Add another link
                                </button>
                            </div>
                        </fieldset>

                        <div className={styles['FieldsetWrapper']}>
                            <fieldset className={styles['Fieldset']}>
                                <legend className={styles['Legend']}>{t('ContactPressForm.legends.type')}</legend>
                                <div className={styles['RadioGroupWrapper']}>
                                    <div className={styles['RadioWrapper']}>
                                        <input
                                            {...register('type', { required: true })}
                                            type="radio"
                                            value="firsttime"
                                            className={styles['Radio']}
                                            id="firsttime"
                                        />
                                        <label className={styles['RadioLabel']} htmlFor="firsttime">
                                            {contact === 'firsttime' && (
                                                <Image
                                                    width={12}
                                                    height={20}
                                                    src={`/img/icon-check.svg`}
                                                    alt=""
                                                    className={styles['Check']}
                                                />
                                            )}
                                            {t('ContactPressForm.labels.firstTime')}
                                        </label>
                                    </div>
                                    <div className={styles['RadioWrapper']}>
                                        <input
                                            {...register('type', { required: true })}
                                            type="radio"
                                            value="repeatvisit"
                                            className={styles['Radio']}
                                            id="repeatvisit"
                                        />
                                        <label className={styles['RadioLabel']} htmlFor="repeatvisit">
                                            {contact === 'repeatvisit' && (
                                                <Image
                                                    width={12}
                                                    height={20}
                                                    src={`/img/icon-check.svg`}
                                                    alt=""
                                                    className={styles['Check']}
                                                />
                                            )}
                                            {t('ContactPressForm.labels.repeatVisit')}
                                        </label>
                                    </div>
                                </div>
                                {errors.type && (
                                    <div>
                                        <ErrorMessage>This field is required</ErrorMessage>
                                    </div>
                                )}
                            </fieldset>
                        </div>

                        <div className={styles['FieldsetWrapper']}>
                            <div className={styles['Fieldset']}>
                                <label htmlFor="when" className={classNames(styles['Legend'], styles['LegendLabel'])}>
                                    {t('ContactPressForm.legends.when')}
                                </label>

                                <div className={styles['Field']}>
                                    <div
                                        className={classNames(styles['InputField'], {
                                            [styles['HasValue']]: watch('when'),
                                            [styles['NoInsideLabel']]: true,
                                            [styles['HasError']]: errors.when,
                                        })}>
                                        <input
                                            id="when"
                                            className={styles['Input']}
                                            placeholder="Type here..."
                                            autoComplete="off"
                                            maxLength={70}
                                            aria-invalid={!!errors.when}
                                            aria-describedby={errors.when ? 'when-error' : null}
                                            {...register('when')}
                                        />
                                    </div>
                                    {errors.when && <ErrorMessage id="when-error">This field is required</ErrorMessage>}
                                </div>
                            </div>
                        </div>

                        <div className={styles['FieldsetWrapper']}>
                            <div className={styles['Fieldset']}>
                                <label htmlFor="how" className={classNames(styles['Legend'], styles['LegendLabel'])}>
                                    {t('ContactPressForm.legends.how')}
                                </label>
                                <div
                                    className={classNames(styles['Field'], styles['TextareaField'], {
                                        [styles['HasValue']]: watch('how'),
                                        [styles['NoInsideLabel']]: true,
                                    })}>
                                    <TextareaAutosize
                                        className={styles['Textarea']}
                                        placeholder="Type away!"
                                        id="how"
                                        {...register('how')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles['PrivacyWrapper']}>
                    <input
                        type="checkbox"
                        className={styles['Checkbox']}
                        name="acceptPrivacy"
                        id="acceptPrivacy"
                        aria-invalid={!!errors.acceptPrivacy}
                        aria-describedby={errors.acceptPrivacy ? 'privacy-error' : null}
                        {...register('acceptPrivacy', { required: true })}
                    />
                    <label
                        dangerouslySetInnerHTML={{
                            __html: formProps?.privacyPolicy?.label,
                        }}
                        className={styles['CheckboxLabel']}
                        htmlFor="acceptPrivacy"
                    />
                </div>
                {errors.acceptPrivacy && <ErrorMessage id="privacy-error">This field is required</ErrorMessage>}

                {!!errors && Object.keys(errors).length > 0 && (
                    <div className={styles['ErrorListWrapper']} role="alert">
                        <ul className={styles['ErrorList']}>
                            {Object.keys(errors).map((key) => {
                                return (
                                    <li key={key} className={styles['ErrorListItem']}>
                                        <Image
                                            width={16}
                                            height={16}
                                            src={`/img/icon-warning.svg`}
                                            alt=""
                                            className={styles['Warning']}
                                        />
                                        <Link href={`#${key}`} className={styles['LinkError']}>
                                            {t(`ContactPressForm.errors.${key}`)}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                <div className={styles['ButtonWrapper']}>
                    <ButtonPrimary
                        type="submit"
                        icon="icon-send"
                        disabled={submitting}
                        modifiers={['InlineFlex', 'CenterText', 'NarrowIconGap', 'BigButton']}>
                        {t('ContactPressForm.labels.send')}
                    </ButtonPrimary>
                </div>
                <small className={styles['ReCaptchaDisclaimer']}>
                    {t('ContactForm.recaptcha.protectedBy')}
                    <a href="https://policies.google.com/privacy">{t('ContactForm.recaptcha.privacyPolicy')}</a>
                    {t('ContactForm.recaptcha.and')}
                    <a href="https://policies.google.com/terms">{t('ContactForm.recaptcha.termsOfService')}</a>
                    {t('ContactForm.recaptcha.apply')}
                </small>
            </form>
            <Dialog
                backdropProps={{ className: styles['Backdrop'] }}
                state={dialog}
                className={`${styles['Dialog']} black-glass`}>
                <DialogHeading className={styles['DialogHeader']}>
                    {t('ContactPressForm.dialog.headline')}
                </DialogHeading>
                <div className={`${styles['DialogContainer']}`}>{t('ContactPressForm.dialog.body')}</div>
                <div className={`${styles['ButtonContainer']}`}>
                    <ButtonPrimary modifiers={['TextAlignCenter']} onClick={dialog.close} asLink={true} href="/">
                        {t('ContactPressForm.dialog.button')}
                    </ButtonPrimary>
                </div>
                <DialogDismiss className={styles['Dismiss']}>
                    <span className="sr-only">{t('ContactPressForm.dialog.button')}</span>
                </DialogDismiss>
            </Dialog>
        </>
    );
};

const ErrorMessage = ({ children, id }) => {
    return (
        <div id={id} className={styles['Error']}>
            <Image width={16} height={16} src={`/img/icon-warning.svg`} alt="" className={styles['Warning']} />
            {children}
        </div>
    );
};

ContactPressForm.propTypes = {};

export default ContactPressForm;
